import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import { useSetAccountCustomerServiceRep } from '~/hooks/apis/loyalty/useCustomerServiceRepAssignments';

import CustomerServiceRepSelector from './CustomerServiceRepSelector';

interface Props {
  open: boolean;
  onClose: () => void;
  customerId: string;
  currentRepId: string | null;
  /** Called after the mutation succeeds so the parent can refresh its account view. */
  onSuccess?: () => void;
}

/** Inline dialog for switching the rep linked to an infinite-tier account. */
export default function LuxLoyaltyChangeRepDialog({ open, onClose, customerId, currentRepId, onSuccess }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [newRepId, setNewRepId] = useState<string | null>(currentRepId);

  useEffect(() => {
    if (open) {
      setNewRepId(currentRepId);
    }
  }, [open, currentRepId]);

  const setRepMutation = useSetAccountCustomerServiceRep({
    onSuccess: () => {
      enqueueSnackbar('Customer service rep updated', { variant: 'success' });
      onSuccess?.();
      onClose();
    },
    onError: (error) => {
      enqueueSnackbar(`Failed to update rep: ${error.message}`, { variant: 'error' });
    },
  });

  const canSubmit = !!newRepId && newRepId !== currentRepId && !setRepMutation.isPending;

  return (
    <Dialog open={open} onClose={setRepMutation.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Change Customer Service Rep</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Typography variant="body2" color="text.secondary">
            Select a new representative to take over this customer&apos;s Infinite Concierge portfolio.
          </Typography>
          <CustomerServiceRepSelector
            value={newRepId}
            onChange={setNewRepId}
            required
            error={!newRepId}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose} disabled={setRepMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            if (newRepId) {
              setRepMutation.mutate({ customerId, customerServiceRepId: newRepId });
            }
          }}
          disabled={!canSubmit}
          startIcon={setRepMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Save changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
